/**
 * Inline expansion of one lineage entry — "what built THIS list?" answered in
 * place, without navigating to it.
 *
 * `FullHistory` shows one level: the chain that produced this list, or the
 * sources it was combined from. When one of those entries was itself built
 * from other lists (`LineageEntry.has_history`), the chevron beside it fetches
 * THAT entry's lineage on demand and renders it nested underneath — sources as
 * a compact `CombinedFromBlock`, otherwise the earlier steps of its chain. The
 * nested rows carry their own chevrons, so a combine of combines unfolds as
 * far as the recruiter cares to go.
 *
 * Two guards keep that recursion finite, both decided without a request:
 *
 * - **Cycle**: `path` holds every id already open above this row (the page's
 *   own import first). An entry whose id is on it gets no chevron — expanding
 *   it would only repeat what is already on screen. `parent_import_id` has no
 *   cycle constraint, so this is reachable data, not a theoretical case.
 * - **Depth**: past `MAX_LINEAGE_EXPAND_DEPTH` nested levels an entry is a
 *   plain link. The list is still one click away; the popover just stops
 *   growing.
 *
 * The fetch is lazy: the child query mounts only once the row is expanded,
 * and it shares `FullHistory`'s cache key, so collapsing and re-expanding (or
 * navigating to the expanded list) renders the cached trail at once; the
 * background refetch on remount only refreshes the contact counts.
 */
import { ChevronRight } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import { Link } from "react-router-dom";

import type { LineageEntry } from "@nu-assist/contracts";

import type { ImportsClient } from "../../../clients/imports.client";
import { formatLineageCount } from "../../../features/imports/selection/importLabels";
import { TAG_DEF_BY_VALUE, deriveTypeTag } from "../imports/tagDefs";
import { CombinedFromBlock } from "./CombinedFromBlock";
import { displayTitle } from "./importTitle";
import { useImportLineage } from "./useImportLineage";

/** Nested levels a recruiter can open below the popover's own rows. */
export const MAX_LINEAGE_EXPAND_DEPTH = 5;

/** Where a row sits in the expansion tree. */
export interface LineageExpandContext {
  client: ImportsClient;
  /** Ids already open above this row, the page's own import first. */
  path: readonly string[];
  /** 0 for the popover's own rows, +1 per nested level. */
  depth: number;
}

/** A combine result — carries the `combined` tag / format. */
export function isCombineEntry(entry: Pick<LineageEntry, "tags" | "import_format">): boolean {
  return entry.import_format === "combined" || (entry.tags ?? []).includes("combined");
}

/** Whether `entry` gets a chevron at this point of the tree. */
export function canExpandLineage(entry: LineageEntry, ctx: LineageExpandContext): boolean {
  return (
    Boolean(entry.has_history) &&
    ctx.depth < MAX_LINEAGE_EXPAND_DEPTH &&
    !ctx.path.includes(entry.id)
  );
}

export interface LineageExpanderProps {
  entry: LineageEntry;
  ctx: LineageExpandContext;
  /** Extra gate from the caller — a chain row only expands a combine, since
   *  an ordinary parent is already the previous row of the same chain. */
  eligible?: boolean;
  /** Renders the row; receives the chevron button, or `null` when this
   *  entry cannot be expanded here. The nested panel goes after it. */
  children: (toggle: ReactNode) => ReactNode;
}

export function LineageExpander({ entry, ctx, eligible = true, children }: LineageExpanderProps) {
  const [expanded, setExpanded] = useState(false);
  const panelId = useId();

  if (!eligible || !canExpandLineage(entry, ctx)) return <>{children(null)}</>;

  const toggle = (
    <button
      type="button"
      data-testid="lineage-expand"
      aria-expanded={expanded}
      aria-controls={panelId}
      aria-label={`Show lists that built ${displayTitle(entry.filename)}`}
      onClick={() => setExpanded((v) => !v)}
      className="inline-flex h-5 w-4 shrink-0 items-center justify-center rounded-sm text-text-muted hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1"
    >
      <ChevronRight
        aria-hidden="true"
        className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-90" : ""}`}
      />
    </button>
  );

  return (
    <>
      {children(toggle)}
      <div
        id={panelId}
        data-testid="lineage-expanded"
        hidden={!expanded}
        className="ml-2 mt-1 border-l border-border pl-3"
      >
        {expanded && (
          <LineageChildren
            importId={entry.id}
            ctx={{ client: ctx.client, path: [...ctx.path, entry.id], depth: ctx.depth + 1 }}
          />
        )}
      </div>
    </>
  );
}

/** The expanded entry's own lineage: its sources, else its earlier chain. */
function LineageChildren({ importId, ctx }: { importId: string; ctx: LineageExpandContext }) {
  const query = useImportLineage(importId, { client: ctx.client });

  if (query.isPending) {
    return (
      <output data-testid="lineage-expand-loading" className="block text-caption text-text-muted">
        Loading…
      </output>
    );
  }
  // A failed background refetch keeps the cached trail on screen (TanStack
  // keeps `data` on a refetch error); the message is only for no trail at all.
  if (query.isError && !query.data) {
    return (
      <p data-testid="lineage-expand-error" className="text-caption text-text-muted">
        Couldn't load this list's history.
      </p>
    );
  }

  const sources = query.data.sources ?? [];
  if (sources.length > 0) {
    return <CombinedFromBlock sources={sources} recipe={null} compact expand={ctx} />;
  }

  // The chain ends with the expanded entry itself, which is already the row
  // above — only what came BEFORE it is new.
  const chain = query.data.chain ?? [];
  const earlier =
    chain.length > 0 && chain[chain.length - 1].id === importId ? chain.slice(0, -1) : chain;
  if (earlier.length === 0) {
    return (
      <p data-testid="lineage-expand-empty" className="text-caption text-text-muted">
        No earlier lists to show.
      </p>
    );
  }

  return (
    <ol data-testid="lineage-expand-chain" className="flex flex-col gap-1.5">
      {earlier.map((entry, i) => (
        <li key={`${entry.id}-${i}`}>
          <LineageExpander entry={entry} ctx={ctx} eligible={isCombineEntry(entry)}>
            {(toggle) => <LineageChainRow entry={entry} toggle={toggle} />}
          </LineageExpander>
        </li>
      ))}
    </ol>
  );
}

function typeDefOf(tags: string[] | undefined, importFormat: string | undefined) {
  const tag = deriveTypeTag(
    (tags ?? []) as Parameters<typeof deriveTypeTag>[0],
    importFormat as Parameters<typeof deriveTypeTag>[1]
  );
  return tag ? TAG_DEF_BY_VALUE[tag] : undefined;
}

/** One compact nested chain step: chevron (or spacer), linked type pill,
 *  title, count. */
function LineageChainRow({ entry, toggle }: { entry: LineageEntry; toggle: ReactNode }) {
  const def = typeDefOf(entry.tags, entry.import_format);
  return (
    <div className="flex items-start gap-1.5">
      {toggle ?? <span aria-hidden="true" className="w-4 shrink-0" />}
      <Link
        data-testid="lineage-chain-link"
        to={`/research/imports/${entry.id}`}
        className={`inline-flex w-fit shrink-0 items-center rounded-pill px-2 py-0.5 text-caption hover:underline ${
          def?.pillClass ?? "bg-surface text-text-secondary"
        }`}
      >
        {def?.label ?? "Import"}
      </Link>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-caption text-text" title={entry.filename}>
          {displayTitle(entry.filename)}
        </span>
        <span
          data-testid="lineage-chain-count"
          className="whitespace-nowrap text-caption text-text-muted"
        >
          {formatLineageCount(entry)}
        </span>
      </span>
    </div>
  );
}
